import React, { useMemo, useContext } from 'react';
import { Box, ListItem, Typography } from '@material-ui/core';
import {
	VENDOR_SCORECARD_PREVIEW_HEADER,
	VENDOR_SCORECARD_PREVIEW_DIM,
	VENDOR_SCORECARD_PREVIEW_DIM_STYLED,
	VENDOR_SCORECARD_PREVIEW_VEN_NUM,
	VENDOR_SCORECARD_PREVIEW_VEN_NUM_STYLED,
	VENDOR_SCORECARD_PREVIEW_VEN_NUM_NORMAL,
	VENDOR_SCORECARD_PREVIEW_READY,
	VENDOR_SCORECARD_PREVIEW_METRIC,
	VENDOR_SCORECARD_PREVIEW_METRIC_STYLED,
	VENDOR_SCORECARD_PREVIEW_METRIC_SELECTED,
	CANNED_REPORT_LIST,
	PREVIEW_INIT_MSG,
	PREVIEW_INIT_MSG_SELECT,
	PREVIEW_INIT_MSG_TIME,
	PREVIEW_INIT_MSG_DIM,
	PREVIEW_INIT_MSG_APPLY_FILTER,
	PREVIEW_READY,
	PREVIEW_READY_BASIC,
	PREVIEW_NOTIFICATION_1,
	PREVIEW_NOTIFICATION_2,
	PREVIEW_READY_CREATE_REPORT,
	STR_DEMAND_LIST,
	PREVIEW_INIT_MSG_TIME_AND_WALMART_ITEM_NUMBER,
	CREATE_READY,
	PREVIEW_PIVOT_NOTIFICATION_1,
	PREVIEW_PIVOT_NOTIFICATION_2,
	PREVIEW_READY_CDS
} from 'constants/constant';
import { FEATURES } from 'helper/basicFeatureFlag';
import { UserPortalContext } from 'context/appcontext';
import { useSelector } from 'react-redux';
import { isCalFamily } from '../filter/FilterCondition';
import styles from './PreviewTable.style';
import scoreCardPreview from 'assets/ScoreCardPreview.svg';
import scoreCardPreviewReady from 'assets/ScoreCardPreviewReady.svg';
import bullet from 'assets/svgs/bullet.svg';
import {
	countDimensionFilter,
	isDefaultFilter,
	hasPivotedColumn,
	hasTimeAcross
} from '../Preview.utils';
import preview_initial_msg from 'assets/svgs/preview_initial_msg.svg';
import preview_ready from 'assets/svgs/preview_ready.svg';
import { getTmplName, isCustomDataset } from 'helper/utils';

function PrintMsg() {
	const portalUserContext = useContext(UserPortalContext);
	const { customerType } = portalUserContext;
	const classes = styles();
	const selectedTemplate = useSelector((state) => state.templateReducer.selectedTemplate);
	const selectedList = useSelector((state) => state.previewReducer.selectedList);
	const filterList = useSelector((state) => state.previewReducer.filterList);
	const previewData = useSelector((state) => state.previewReducer.previewData);
	const templateData = useSelector((state) => state.templateReducer.templateData);
	const pivotOpts = useSelector((state) => state.previewReducer.pivotOpts);
	const pivotColumn = pivotOpts?.pivotColumn;
	const timeAcrossEnabled = hasTimeAcross(pivotOpts);
	// eslint-disable-next-line no-unused-vars
	const { DISABLE_PREVIEW: disablePreview } = FEATURES[customerType].PREVIEW_PAGE;
	const sbuDBTechNames = useSelector((state) => state.templateReducer.sbuDimDBTechName);

	const isTimeReq = useMemo(() => {
		return templateData.filter((item) => item.templateName === getTmplName(selectedTemplate))[0]
			?.time;
	}, [selectedTemplate]);

	const isCalFilterSelected = useMemo(() => {
		// if time not required then return true, to not check for time filter
		if (!isTimeReq) {
			return true;
		}
		return filterList?.some((el) => isCalFamily(el) && el.field !== undefined);
	}, [filterList]);

	/** Custom Attr also consider as dimension. */
	const isDimenSelected = useMemo(() => {
		return selectedList?.some((el) => ['dimensions', 'customAttribute'].includes(el.parent));
	}, [selectedList]);

	const isFilterApplied = useMemo(() => {
		return countDimensionFilter(filterList).length > 0;
	}, [filterList]);

	const isMeasureSelected = useMemo(() => {
		return selectedList?.some((el) => el.parent === 'measures');
	}, [selectedList]);

	const areRequiredFiltersApplied = useMemo(() => {
		let isFilterApplied = true;
		filterList.forEach((item) => {
			if (isDefaultFilter(item, selectedList, sbuDBTechNames, selectedTemplate)) {
				isFilterApplied &= item.field !== undefined;
			}
		});
		return isFilterApplied;
	}, [filterList, selectedList]);

	const shouldPrintMsg = useMemo(() => {
		if (CANNED_REPORT_LIST.includes(Number(selectedTemplate))) {
			return true;
		}
		if (previewData?.jsonResponse.length === 0) {
			return true;
		}
		if (!isCalFilterSelected) {
			return true;
		}
		if (!(isDimenSelected || isMeasureSelected)) {
			return true;
		}

		return false;
	}, [filterList, selectedList, previewData]);

	const printMsg = () => {
		if (CANNED_REPORT_LIST.includes(Number(selectedTemplate))) {
			if (isFilterApplied && isMeasureSelected) {
				return (
					<Box
						className={`${classes.msgContainer} ${classes.msgWithImageContainer}`}
						style={{ zIndex: 1 }}>
						<img src={scoreCardPreviewReady}></img>
						<ListItem className={classes.bulletTextHeader}>
							{VENDOR_SCORECARD_PREVIEW_READY}
						</ListItem>
					</Box>
				);
			} else {
				return (
					<Box
						className={`${classes.msgContainer} ${classes.msgWithImageContainer}`}
						style={{ zIndex: 1 }}>
						<img src={scoreCardPreview}></img>
						<ListItem className={classes.bulletTextHeader}>
							{VENDOR_SCORECARD_PREVIEW_HEADER}
						</ListItem>
						<ListItem className={classes.bulletText}>
							<img src={bullet} style={{ height: '6px', paddingRight: '16px' }}></img>
							<span className={classes.showIcon}>{VENDOR_SCORECARD_PREVIEW_DIM}&nbsp;</span>
							<span className={classes.highlightedFont}>{VENDOR_SCORECARD_PREVIEW_DIM_STYLED}</span>
							<span>&nbsp;{VENDOR_SCORECARD_PREVIEW_METRIC}&nbsp;</span>
							<span className={classes.highlightedFont}>
								{VENDOR_SCORECARD_PREVIEW_METRIC_STYLED}
							</span>
							<span>&nbsp;{VENDOR_SCORECARD_PREVIEW_METRIC_SELECTED}</span>
						</ListItem>
						<ListItem className={classes.bulletText}>
							<img src={bullet} style={{ height: '6px', paddingRight: '16px' }}></img>
							<span className={classes.showIcon}>
								{`${VENDOR_SCORECARD_PREVIEW_VEN_NUM}`}&nbsp;
							</span>
							<span className={classes.highlightedFont}>
								{VENDOR_SCORECARD_PREVIEW_VEN_NUM_STYLED}
							</span>
							<span className={classes.showIcon}>
								&nbsp;{VENDOR_SCORECARD_PREVIEW_VEN_NUM_NORMAL}
							</span>
						</ListItem>
					</Box>
				);
			}
		}

		if (isCalFilterSelected && isDimenSelected && areRequiredFiltersApplied) {
			const isCDSReport = isCustomDataset(selectedTemplate);

			return (
				<Box
					className={`${classes.msgContainer} ${classes.msgWithImageContainer}`}
					style={{ zIndex: 1 }}>
					{!timeAcrossEnabled && <img style={{ marginLeft: '-80px' }} src={preview_ready}></img>}
					{disablePreview ? (
						<ListItem className={classes.bulletTextHeader}>
							{
								<Typography
									className={classes.bulletTextHeader}
									style={{ color: '#3C228A', paddingRight: '5px', textTransform: 'capitalize' }}>
									{PREVIEW_READY_CREATE_REPORT}
								</Typography>
							}
							{PREVIEW_READY_BASIC}
						</ListItem>
					) : (
						<>
							{hasPivotedColumn(pivotColumn) ? (
								<>
									{!timeAcrossEnabled && (
										<>
											<ListItem className={classes.bulletTextHeader}>{CREATE_READY}</ListItem>
											<ListItem className={classes.bulletText}>
												<span className={classes.showIcon}>
													{PREVIEW_PIVOT_NOTIFICATION_1}&nbsp;
												</span>
											</ListItem>
											<ListItem className={classes.bulletText}>
												<span className={classes.showIcon}>{PREVIEW_PIVOT_NOTIFICATION_2}</span>
											</ListItem>
										</>
									)}
								</>
							) : (
								<>
									<ListItem className={classes.bulletTextHeader}>
										{isCDSReport ? PREVIEW_READY_CDS : PREVIEW_READY}
									</ListItem>
									{/* Do not display the following to the user if it is CDS */}
									{!isCDSReport && (
										<>
											<ListItem className={classes.bulletText}>
												<span className={classes.showIcon}>{PREVIEW_NOTIFICATION_1}&nbsp;</span>
											</ListItem>
											<ListItem className={classes.bulletText}>
												<span className={classes.showIcon}>{PREVIEW_NOTIFICATION_2}</span>
											</ListItem>
										</>
									)}
								</>
							)}
						</>
					)}
				</Box>
			);
		}

		const isStoreDemandSelected = STR_DEMAND_LIST.includes(Number(selectedTemplate));
		const filterText = isStoreDemandSelected
			? PREVIEW_INIT_MSG_TIME_AND_WALMART_ITEM_NUMBER
			: PREVIEW_INIT_MSG_TIME;

		return (
			<Box
				className={`${classes.msgContainer} ${classes.msgWithImageContainer}`}
				style={{ zIndex: 1 }}>
				<img style={{ marginLeft: '-80px' }} src={preview_initial_msg}></img>
				<ListItem className={classes.bulletTextHeader}>{PREVIEW_INIT_MSG}</ListItem>
				<ListItem className={classes.bulletText}>
					<img src={bullet} style={{ height: '6px', paddingRight: '16px' }}></img>
					<span className={classes.showIcon}>{PREVIEW_INIT_MSG_SELECT}&nbsp;</span>
					<span className={classes.highlightedFont}>{PREVIEW_INIT_MSG_DIM}</span>
				</ListItem>
				<ListItem className={classes.bulletText}>
					<img src={bullet} style={{ height: '6px', paddingRight: '16px' }}></img>
					<span className={classes.showIcon}>{PREVIEW_INIT_MSG_APPLY_FILTER}&nbsp;</span>
					<span className={classes.highlightedFont}>{filterText}</span>
				</ListItem>
			</Box>
		);
	};

	if (!shouldPrintMsg) {
		return null;
	}

	return (
		<Box className={`${classes.msgContainer} ${classes.msgWithImageContainer}`}>{printMsg()}</Box>
	);
}

export default PrintMsg;
